import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import {AuthenticationService} from "./authentication.service";

export interface ReportResponse {
  content: string;
  generatedAt: string;
}

export interface AgentDebugMetrics {
  model?: string;
  serverElapsedMs?: number;
  dataCollectionMs?: number;
  ollamaTotalMs?: number;
  loadMs?: number;
  promptTokens?: number;
  outputTokens?: number;
  outputTokensPerSecond?: number;
  outputTokensPerMinute?: number;
  doneReason?: string;
}

export type AgentStreamEvent =
  | { type: 'chunk'; content: string }
  | { type: 'meta'; metrics: AgentDebugMetrics };

@Injectable({ providedIn: 'root' })
export class AgentService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);
  private readonly baseUrl = `${environment.localApiUrl}agent/report`;

  // Risposta completa
  generateReport(prompt: string, orgId: number): Observable<ReportResponse> {
    return this.http.post<ReportResponse>(`${this.baseUrl}/generate`, { prompt, orgId });
  }

  // Streaming via SSE – emette chunk di testo man mano che arrivano
  streamReport(prompt: string, orgId: number): Observable<AgentStreamEvent> {
    const subject = new Subject<AgentStreamEvent>();

    // Leggi il token dallo stesso posto che usa il tuo interceptor
    // (localStorage, sessionStorage, un AuthService, ecc.)
    const token = this.authService.currentUserValue.jwtToken;

    const url = `${this.baseUrl}/stream/${orgId}?prompt=${encodeURIComponent(prompt)}`;

    // EventSource non supporta header → usa fetch con ReadableStream
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream'
      }
    }).then(response => {
      if (!response.ok) {
        response.text()
          .then(body => subject.error(new Error(this.formatHttpError(response.status, body))))
          .catch(() => subject.error(new Error(`HTTP ${response.status}`)));
        return;
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const read = () => {
        reader.read().then(({ done, value }) => {
          if (done) { subject.complete(); return; }

          buffer += decoder.decode(value, { stream: true });

          const blocks = buffer.split('\n\n');
          buffer = blocks.pop() ?? '';

          for (const block of blocks) {
            const event = this.parseSseEvent(block);
            if (!event) continue;

            if (event.event === 'done' || event.data === '[DONE]') {
              subject.complete();
              reader.cancel();
              return;
            }

            if (event.event === 'meta') {
              subject.next({
                type: 'meta',
                metrics: JSON.parse(event.data) as AgentDebugMetrics
              });
              continue;
            }

            subject.next({
              type: 'chunk',
              content: event.data.replace(/\\n/g, '\n')
            });
          }

          read(); // continua a leggere
        }).catch(err => subject.error(err));
      };

      read();
    }).catch(err => subject.error(err));

    return subject.asObservable();
  }

  private formatHttpError(status: number, body: string): string {
    if (!body) return `HTTP ${status}`;

    try {
      const parsed = JSON.parse(body);
      if (typeof parsed?.message === 'string' && parsed.message.trim()) {
        return parsed.message;
      }
    } catch {
      // Ignore non-JSON error bodies and fall back to raw text.
    }

    return body.trim() || `HTTP ${status}`;
  }

  private parseSseEvent(block: string): { event: string; data: string } | null {
    const lines = block.split('\n');
    let eventName = 'chunk';
    const dataLines: string[] = [];

    for (const rawLine of lines) {
      const line = rawLine.replace(/\r$/, '');
      if (!line) continue;

      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
        continue;
      }

      if (line.startsWith('data:')) {
        dataLines.push(line.length > 5 && line.charAt(5) === ' '
          ? line.slice(6)
          : line.slice(5));
      }
    }

    if (!dataLines.length && eventName !== 'done') return null;

    return {
      event: eventName,
      data: dataLines.join('\n')
    };
  }
}
